import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { NavigateLinks } from '@/components';
import { icons } from '@/assets';
import { childrenBusinessPageBreadcrumbs } from '@/pages/Business/config';
import {
  usePickupCycles,
  useBranchesMap,
  usePickupCycleActions,
} from '@/pages/Business/pages/PickupCycles/hooks';
import {
  PickupCyclesTable,
  PickupCyclesToolbar,
  PickupCycleModal,
  BranchAddressModal,
} from '@/pages/Business/pages/PickupCycles/components';
import {
  BulkCreatePickupCyclePayload,
  PickupCycle,
} from '@/pages/Business/pages/PickupCycles/models';
import { buildPickupCycleRows } from '@/pages/Business/pages/PickupCycles/utils/rows';

export const PickupCycles = () => {
  const navigate = useNavigate();
  const breadcrumbs = useMemo(
    () => [
      ...childrenBusinessPageBreadcrumbs(navigate),
      { label: 'Retiros Flex', to: '/business/pickup' },
    ],
    [navigate],
  );

  const [isCycleModalOpen, setIsCycleModalOpen] = useState(false);
  const [selectedCycle, setSelectedCycle] = useState<PickupCycle | null>(null);
  const [addressBranchId, setAddressBranchId] = useState<string | null>(null);
  const [isAddressModalOpen, setIsAddressModalOpen] = useState(false);
  const [branchesReload, setBranchesReload] = useState(0);

  const {
    cycles,
    isLoading,
    totalItems,
    totalPages,
    currentPage,
    pageSize,
    commerceFilter,
    branchFilter,
    tagFilter,
    cycleTimeFilter,
    daysFilter,
    hasActiveFilters,
    handlePageChange,
    handlePageSizeChange,
    handleCommerceFilterChange,
    handleBranchFilterChange,
    handleTagFilterChange,
    handleCycleTimeFilterChange,
    handleDaysFilterChange,
    clearFilter,
  } = usePickupCycles();

  const commerceIds = useMemo(
    () => cycles.map((cycle) => cycle.commerceId),
    [cycles],
  );

  const { branchesByCommerce, isLoading: isBranchesLoading } = useBranchesMap(
    commerceIds,
    branchesReload,
  );

  const rows = useMemo(
    () => buildPickupCycleRows(cycles, branchesByCommerce),
    [cycles, branchesByCommerce],
  );

  const tableLoading = isLoading || isBranchesLoading;

  const { handleCreateBulk, handleDelete, handleEdit, isSubmitting } =
    usePickupCycleActions();

  const handleOpenCreate = () => {
    setSelectedCycle(null);
    setIsCycleModalOpen(true);
  };

  const handleOpenEdit = (cycle: PickupCycle) => {
    setSelectedCycle(cycle);
    setIsCycleModalOpen(true);
  };

  const handleCloseCycleModal = () => {
    setIsCycleModalOpen(false);
    setSelectedCycle(null);
  };

  const handleSubmit = (payload: BulkCreatePickupCyclePayload) => {
    const { branchIds, ...rest } = payload;

    // Editar sigue siendo de a una: el modal manda una sola sucursal.
    if (selectedCycle) {
      handleEdit(
        selectedCycle,
        { ...rest, branchId: branchIds[0] ?? null },
        handleCloseCycleModal,
      );
      return;
    }

    handleCreateBulk(payload, handleCloseCycleModal);
  };

  const handleOpenAddressModal = (branchId: string) => {
    setAddressBranchId(branchId);
    setIsAddressModalOpen(true);
  };

  const handleCloseAddressModal = () => {
    setIsAddressModalOpen(false);
    setAddressBranchId(null);
  };

  const handleAddressSaved = () => {
    setBranchesReload((prev) => prev + 1);
  };

  const hasNoResults = !tableLoading && cycles.length === 0;

  return (
    <section className='flex flex-col w-full h-full gap-8'>
      <NavigateLinks actualPage='Retiros agendados' breadcrumbs={breadcrumbs} />

      <div className='flex flex-col gap-5'>
        <div className='flex flex-col gap-1'>
          <h3 className='flex items-center gap-2 text-2xl font-bold text-gray-800'>
            <img
              src={icons.vehicles}
              alt='Icono de retiros agendados'
              className='w-6 h-6'
            />
            Al agendar un retiro para determinados días y hora, el sistema
            generará automáticamente los retiros correspondientes en esos días.
          </h3>
        </div>

        <PickupCyclesToolbar
          commerceFilter={commerceFilter}
          branchFilter={branchFilter}
          tagFilter={tagFilter}
          cycleTimeFilter={cycleTimeFilter}
          daysFilter={daysFilter}
          hasActiveFilters={hasActiveFilters}
          onCommerceFilterChange={handleCommerceFilterChange}
          onBranchFilterChange={handleBranchFilterChange}
          onTagFilterChange={handleTagFilterChange}
          onCycleTimeFilterChange={handleCycleTimeFilterChange}
          onDaysFilterChange={handleDaysFilterChange}
          onClearFilter={clearFilter}
          onNewCycle={handleOpenCreate}
        />
      </div>

      <div className='flex flex-col gap-4'>
        <PickupCyclesTable
          rows={rows}
          isLoading={tableLoading}
          totalItems={totalItems}
          totalPages={totalPages}
          currentPage={currentPage}
          pageSize={pageSize}
          onPageChange={handlePageChange}
          onPageSizeChange={handlePageSizeChange}
          onEdit={handleOpenEdit}
          onDelete={handleDelete}
          onConfigureAddress={handleOpenAddressModal}
        />

        {hasNoResults && (
          <div className='flex flex-col items-center justify-center p-12 bg-gray-50 rounded-lg border-2 border-dashed border-gray-200'>
            <p className='text-xl font-semibold text-gray-600'>
              No hay retiros agendados todavía
            </p>
          </div>
        )}
      </div>

      <PickupCycleModal
        open={isCycleModalOpen}
        onClose={handleCloseCycleModal}
        onSubmit={handleSubmit}
        initialData={selectedCycle}
        isSubmitting={isSubmitting}
        onConfigureAddress={handleOpenAddressModal}
      />

      <BranchAddressModal
        open={isAddressModalOpen}
        branchId={addressBranchId}
        onClose={handleCloseAddressModal}
        onSaved={handleAddressSaved}
      />
    </section>
  );
};

export default PickupCycles;
